import { useState, useCallback } from 'react';
import api from '../api/client';

/**
 * useUpdateSeedsOrder
 * Hook for viewing and updating seeds orders (status / lbqty).
 *
 * Key difference from useUpdateOrderStatus:
 *   Seeds delivered (D) do NOT auto-add to stock — no stock logic here.
 *
 * @param {Function} onSuccess — callback after successful save
 */
export const useUpdateSeedsOrder = ({ onSuccess }) => {
  const [orderID,      setOrderID]      = useState('');
  const [year,         setYear]         = useState('');
  const [monthNum,     setMonthNum]     = useState('');
  const [orderRows,    setOrderRows]    = useState([]);
  const [editedRows,   setEditedRows]   = useState([]);
  const [orderIDs,     setOrderIDs]     = useState([]);
  const [isLoading,    setIsLoading]    = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error,        setError]        = useState('');

  const fetchOrderIDs = useCallback(async () => {
    try {
      const res = await api.get('/api/material-orders/seeds-order-ids');
      setOrderIDs(res.data);
    } catch {
      console.error('Failed to fetch seeds order IDs');
    }
  }, []);

  const fetchOrderRows = useCallback(async (oid) => {
    const id = oid ?? orderID;
    if (!id?.trim()) { setError('Please enter an Order ID'); return; }

    setIsLoading(true);
    setError('');
    setSubmitResult(null);

    try {
      const res = await api.get('/api/material-orders/seeds-detail', {
        params: { orderID: id.trim() },
      });

      if (res.data.length === 0) {
        setError('No orders found for this Order ID');
        setOrderRows([]);
        setEditedRows([]);
      } else {
        setOrderRows(res.data);
        setEditedRows(res.data.map(r => ({
          ...r,
          _statusID:     r.statusID,
          _lbqty:        r.lbqty,
          _prevStatusID: r.statusID,
        })));
      }
    } catch {
      setError('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  }, [orderID]);

  const updateRow = useCallback((index, field, value) => {
    setEditedRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }, []);

  // Строки, уже сохранённые как D (получены), не трогаем — смена статуса
  // с D откатывает сток; для этого есть построчный селект.
  const applyStatusToAll = useCallback((statusID) => {
    setEditedRows(prev => prev.map(r => (r.statusID === 'D' ? r : { ...r, _statusID: statusID })));
  }, []);

  const submitChanges = useCallback(async () => {
    const changed = editedRows.filter((r, i) =>
      r._statusID !== orderRows[i].statusID ||
      parseFloat(r._lbqty) !== parseFloat(orderRows[i].lbqty)
    );

    if (changed.length === 0) {
      setSubmitResult({ success: false, message: 'No changes to save' });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const rows = changed.map(r => ({
        seedsID:           r.seedsID,
        year:              r.year,
        month_num:         r.month_num,
        statusID:          r._statusID,
        lbqty:             parseFloat(r._lbqty),
        price_per_pcs:     r.price_per_pcs,
        est_del_date:      r.est_del_date,
        bo_follow_up_date: r.bo_follow_up_date,
      }));

      await api.put('/api/material-orders/seeds-update', { rows });
      setSubmitResult({ success: true, message: `Updated ${changed.length} row(s) successfully.` });
      await fetchOrderRows(orderID);
      if (onSuccess) onSuccess();
    } catch (e) {
      setSubmitResult({ success: false, message: e.response?.data?.error || 'Failed to update orders' });
    } finally {
      setIsSubmitting(false);
    }
  }, [editedRows, orderRows, orderID, fetchOrderRows, onSuccess]);

  const deleteRow = useCallback(async (seedsID, rowYear, rowMonth) => {
    await api.delete('/api/material-orders/delete-item', {
      data: { isSeeds: true, seedsID, year: parseInt(rowYear), month_num: parseInt(rowMonth) },
    });
    await fetchOrderRows();
  }, [fetchOrderRows]);

  const reset = useCallback(() => {
    setOrderID('');
    setYear('');
    setMonthNum('');
    setOrderRows([]);
    setEditedRows([]);
    setError('');
    setSubmitResult(null);
  }, []);

  const hasChanges = editedRows.some((r, i) =>
    r._statusID !== orderRows[i]?.statusID ||
    parseFloat(r._lbqty) !== parseFloat(orderRows[i]?.lbqty)
  );

  return {
    orderID, setOrderID,
    year, setYear,
    monthNum, setMonthNum,
    orderIDs,
    orderRows,
    editedRows,
    isLoading,
    isSubmitting,
    submitResult,
    error,
    hasChanges,
    fetchOrderIDs,
    fetchOrderRows,
    updateRow,
    applyStatusToAll,
    submitChanges,
    deleteRow,
    reset,
  };
};
